/**
 * Task rules (step 9.3 of docs/plans/ledger.md, D6 of the Phase 9 brief): a
 * record says "when an entry is saved (or approved) and this holds, give
 * someone a task, due on a day". It is what the Link pattern `reminder` was
 * meant to do, and it writes `data.task` instead of another entry.
 *
 *   on        'save' (the default): when the entry counts, on a save or when
 *             it is approved after waiting; 'approve': only when approved
 *   when      the trigger grammar a Link had (`triggerHolds`, `readTrigger`
 *             in links.ts, which 9b moves to rules/trigger.ts): an AND of ORs
 *   assignee  the owners, the person who wrote the entry, the members of a
 *             role, or the member a field of the entry names
 *   title     the words of the task; `{<field id>}` says that field's value
 *   due       a number of days after a date field of the entry, or after the
 *             day it fires; absent: due the day it fires
 *
 * The live rules sit in `app.record.prefs.tasks`, by ids. A setup writes them
 * by keys (`SpecTaskRule`), and apply rewrites them. Pure.
 */
import { opIsNumeric, opNeedsValue, readTrigger, TRIGGER_CONDITION_OPS, type TriggerConditionOp, type Trigger } from './rules/trigger';

export type TaskRuleAssignee =
  | { readonly kind: 'owner' }
  | { readonly kind: 'writer' }
  | { readonly kind: 'role'; readonly roleId: string }
  | { readonly kind: 'field'; readonly fieldId: string };

export type TaskRuleDue = {
  /** A date field of the entry the days count from. Absent: the day the rule fires. */
  readonly from?: string;
  /** Days after (or, below zero, before) that day. */
  readonly days: number;
};

export type TaskRuleOn = 'save' | 'approve';

/** One live rule, by ids, as `app.record.prefs.tasks` keeps it. */
export type TaskRule = {
  readonly id: string;
  readonly on: TaskRuleOn;
  readonly when: Trigger;
  readonly assignee: TaskRuleAssignee;
  readonly title: string;
  readonly due?: TaskRuleDue;
};

/** The most rules one record keeps. */
export const TASK_RULES_MAX = 8;
/** The most days a due moves. */
export const TASK_DUE_DAYS_MAX = 3660;
/** The longest title, in characters. */
export const TASK_TITLE_MAX = 160;

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isName = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';
const isDays = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && Math.abs(value) <= TASK_DUE_DAYS_MAX;

/** An assignee, by ids or keys alike, or null. */
export const readTaskAssignee = (raw: unknown): TaskRuleAssignee | null => {
  if (!isObject(raw)) return null;
  if (raw.kind === 'owner' || raw.kind === 'writer') return { kind: raw.kind };
  if (raw.kind === 'role' && isName(raw.roleId)) return { kind: 'role', roleId: raw.roleId };
  if (raw.kind === 'field' && isName(raw.fieldId)) return { kind: 'field', fieldId: raw.fieldId };
  return null;
};

/** A due, or undefined when absent, or null when it does not read. */
export const readTaskDue = (raw: unknown): TaskRuleDue | undefined | null => {
  if (raw === undefined || raw === null) return undefined;
  if (!isObject(raw) || !isDays(raw.days)) return null;
  if (raw.from !== undefined && !isName(raw.from)) return null;
  return isName(raw.from) ? { from: raw.from, days: raw.days } : { days: raw.days };
};

/** One live rule, checked without trusting the caller, or null. */
export const readTaskRule = (raw: unknown): TaskRule | null => {
  if (!isObject(raw) || !isName(raw.id)) return null;
  const on: TaskRuleOn | null = raw.on === undefined || raw.on === 'save' ? 'save' : raw.on === 'approve' ? 'approve' : null;
  const when = readTrigger(raw.when);
  const assignee = readTaskAssignee(raw.assignee);
  const due = readTaskDue(raw.due);
  if (on === null || when === null || assignee === null || due === null) return null;
  if (!isName(raw.title) || raw.title.length > TASK_TITLE_MAX) return null;
  return { id: raw.id, on, when, assignee, title: raw.title, ...(due === undefined ? {} : { due }) };
};

/** A record's live rules (`prefs.tasks`): each that reads, in order, and none past the most. A rule that does not read is dropped. */
export const readTaskRules = (raw: unknown): TaskRule[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((one) => {
    const rule = readTaskRule(one);
    return rule === null ? [] : [rule];
  }).slice(0, TASK_RULES_MAX);
};

/** Whether a rule fires for this moment: a save that counts fires `save` rules, an approval fires both. */
export const ruleFiresOn = (rule: Pick<TaskRule, 'on'>, moment: 'save' | 'approve'): boolean => moment === 'approve' || rule.on === 'save';

/** `YYYY-MM-DD` plus some days, in the calendar. */
const addDays = (day: string, days: number): string => {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

/**
 * The day the task is due: `due.from`'s date on the entry (or `today`) moved
 * by `due.days`. Null when it counts from a date the entry left empty: the
 * rule then raises no task, as a reminder of nothing would say nothing.
 */
export const taskDueOf = (rule: Pick<TaskRule, 'due'>, values: Readonly<Record<string, unknown>>, today: string): string | null => {
  if (rule.due === undefined) return today;
  if (rule.due.from === undefined) return addDays(today, rule.due.days);
  const held = values[rule.due.from];
  const day = typeof held === 'string' && /^\d{4}-\d{2}-\d{2}/.test(held) ? held.slice(0, 10) : held instanceof Date ? held.toISOString().slice(0, 10) : null;
  return day === null ? null : addDays(day, rule.due.days);
};

/** Every field a title names in braces. */
export const titleFieldsOf = (title: string): string[] => [...new Set([...title.matchAll(/\{([^{}]+)\}/g)].map((one) => one[1]!.trim()))];

/** The title with each `{field}` said as `say` words it; a field it cannot say is said as nothing. */
export const taskTitleOf = (title: string, say: (fieldId: string) => string | null): string =>
  title.replace(/\{([^{}]+)\}/g, (_all, named: string) => say(named.trim()) ?? '').replace(/\s{2,}/g, ' ').trim();

/* ---------------------------------------------------------------- the spec */

/** One condition of a task rule a setup writes, by keys: on a select, `is` names a choice key. */
export type SpecTaskCondition = { readonly fieldKey: string; readonly op: TriggerConditionOp; readonly value?: string | number | boolean };

/** A task rule as a setup writes it: by field keys, every condition must hold. */
export type SpecTaskRule = {
  readonly on?: TaskRuleOn;
  readonly when: readonly SpecTaskCondition[];
  /** The owners, the writer, or the member a field names (a role is set once the team is in). */
  readonly assignee: { readonly kind: 'owner' } | { readonly kind: 'writer' } | { readonly kind: 'field'; readonly fieldKey: string };
  /** The task's words; `{key}` says a field's value. */
  readonly title: string;
  readonly due?: { readonly fromFieldKey?: string; readonly days: number };
};

/** A spec rule read off the wire (an op, a composed setup), or a sentence saying what is wrong with its shape. */
export const readSpecTaskRule = (raw: unknown): SpecTaskRule | string => {
  if (!isObject(raw)) return 'an object with when, assignee and title';
  const on = raw.on === undefined ? undefined : raw.on === 'save' || raw.on === 'approve' ? raw.on : null;
  if (on === null) return 'on as save or approve';
  const when: SpecTaskCondition[] = [];
  for (const [index, one] of (Array.isArray(raw.when) ? raw.when : raw.when === undefined ? [] : [null]).entries()) {
    if (!isObject(one) || !isName(one.fieldKey) || typeof one.op !== 'string') return `when[${index}] as { fieldKey, op, value? }`;
    const value = one.value;
    if (value !== undefined && typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') return `when[${index}].value as one value`;
    when.push({ fieldKey: one.fieldKey, op: one.op as TriggerConditionOp, ...(value === undefined ? {} : { value }) });
  }
  const assignee = raw.assignee;
  if (!isObject(assignee)) return 'assignee as the owner, the writer, or a field';
  const who: SpecTaskRule['assignee'] | null =
    assignee.kind === 'owner' || assignee.kind === 'writer'
      ? { kind: assignee.kind }
      : assignee.kind === 'field' && isName(assignee.fieldKey)
        ? { kind: 'field', fieldKey: assignee.fieldKey }
        : null;
  if (who === null) return 'assignee as the owner, the writer, or a field';
  if (!isName(raw.title) || raw.title.length > TASK_TITLE_MAX) return `a title of 1 to ${TASK_TITLE_MAX} characters`;
  let due: SpecTaskRule['due'];
  if (raw.due !== undefined && raw.due !== null) {
    if (!isObject(raw.due) || !isDays(raw.due.days) || (raw.due.fromFieldKey !== undefined && !isName(raw.due.fromFieldKey))) return 'due as { fromFieldKey?, days }';
    due = isName(raw.due.fromFieldKey) ? { fromFieldKey: raw.due.fromFieldKey, days: raw.due.days } : { days: raw.due.days };
  }
  return { ...(on === undefined ? {} : { on }), when, assignee: who, title: raw.title, ...(due === undefined ? {} : { due }) };
};

/** One field as the check needs it. */
export type TaskRuleField = { readonly key: string; readonly type: string; readonly options?: readonly { readonly key: string }[] };

/**
 * What keeps one condition from reading as the live rule reads it
 * (`readTrigger`), or null: a known op, a value only where the op takes one,
 * a figure for a numeric op, and words or a number, never a yes-or-no. A
 * condition the live rule cannot read would drop the whole rule, so the task
 * would never fire.
 */
const conditionProblem = (condition: SpecTaskCondition): string | null => {
  const op = condition.op as string;
  if (!(TRIGGER_CONDITION_OPS as readonly string[]).includes(op)) return `The task rule compares "${condition.fieldKey}" with "${op}", which is no condition: use one of ${TRIGGER_CONDITION_OPS.join(', ')}.`;
  if (typeof condition.value === 'boolean') return `The task rule compares "${condition.fieldKey}" with a yes-or-no; say it as the words "true" or "false".`;
  if (opNeedsValue(condition.op) && condition.value === undefined) return `The task rule's "${op}" on "${condition.fieldKey}" needs a value.`;
  if (opIsNumeric(condition.op) && !Number.isFinite(typeof condition.value === 'string' && condition.value.trim() !== '' ? Number(condition.value) : condition.value)) {
    return `The task rule's "${op}" on "${condition.fieldKey}" compares a number, not "${String(condition.value)}".`;
  }
  const values = !opNeedsValue(condition.op) || condition.value === undefined ? [] : [condition.value];
  if (readTrigger({ all: [{ any: [{ fieldId: condition.fieldKey, op, values }] }] }) === null) return `The task rule's "${op}" on "${condition.fieldKey}" does not take "${String(condition.value)}".`;
  return null;
};

/**
 * Every problem with a record's task rule, as sentences, or none: it sits on
 * a record, every field it names is the record's own, a due counts from a
 * date, and a field assignee and a choice it compares are real.
 */
export const specTaskRuleProblems = (rule: SpecTaskRule, record: { readonly key: string; readonly kind: string; readonly fields: readonly TaskRuleField[] }): string[] => {
  const problems: string[] = [];
  const fieldOf = (key: string): TaskRuleField | undefined => record.fields.find((one) => one.key === key);
  if (record.kind !== 'record') problems.push(`"${record.key}" is a list; a task is raised from a record's entries.`);
  for (const condition of rule.when) {
    const field = fieldOf(condition.fieldKey);
    if (field === undefined) {
      problems.push(`The task rule asks about "${condition.fieldKey}", which "${record.key}" does not hold.`);
      continue;
    }
    const problem = conditionProblem(condition);
    if (problem !== null) {
      problems.push(problem);
      continue;
    }
    if (field.type === 'select' && (condition.op === 'is' || condition.op === 'is_not') && typeof condition.value === 'string' && !(field.options ?? []).some((one) => one.key === condition.value)) {
      problems.push(`"${condition.value}" is not a choice of "${condition.fieldKey}".`);
    }
  }
  if (rule.assignee.kind === 'field' && fieldOf(rule.assignee.fieldKey) === undefined) problems.push(`The task goes to "${rule.assignee.fieldKey}", which "${record.key}" does not hold.`);
  if (rule.due?.fromFieldKey !== undefined) {
    const from = fieldOf(rule.due.fromFieldKey);
    if (from === undefined || (from.type !== 'date' && from.type !== 'datetime')) problems.push(`A task is due a number of days from a date; "${rule.due.fromFieldKey}" is not a date of "${record.key}".`);
  }
  for (const named of titleFieldsOf(rule.title)) {
    if (fieldOf(named) === undefined) problems.push(`The task's words name "${named}", which "${record.key}" does not hold.`);
  }
  return problems;
};
